/**
 * repaginate.ts — design.md §5.3, and the acceptance test for the entire product.
 *
 *   "Change the font size mid-narration. The page reflows, the correct page is showing, the
 *    highlight is still on the spoken sentence, and the audio does not seek or gap. It falls out
 *    for free because the position was never a page number and the audio was never told about
 *    layout."
 *
 * The order of the seven steps below is not stylistic. Each one is placed where it is because
 * moving it produces a specific visible bug:
 *
 *   1. PIN FIRST — before any style is touched. Read the pin after `applyTypography()` and you are
 *      reading a column map that describes the *old* type against a layout that has already been
 *      invalidated; the browser will happily answer, with the wrong sentence.
 *   2. Kill the transition — otherwise step 5's `goTo` animates a 220 ms slide across however many
 *      columns the size change moved the reader, which reads as the book throwing itself sideways.
 *   3. Apply typography — the only write.
 *   4. ONE forced layout — `Paginator.measure()`. This is the number that decides whether the
 *      riskiest bet in the design was right (§13.1), which is why step 7 exists.
 *   5. `goTo(columnOf(pin))`, `animate: false`.
 *   6. Restore the transition on the NEXT frame, not this one. Restoring it synchronously puts the
 *      style change in the same task as step 5's, and the browser coalesces them into a transition
 *      from the old transform after all.
 *   7. Beacon.
 */

import type { Paginator } from "./Paginator.ts";

export type RepaginateCause =
  | "mount"
  | "part"
  | "resize"
  | "rotate"
  | "typography"
  | "fonts"
  | "images"
  | "visibility";

/** Where to land when the pin cannot be honoured, or when navigation asked for something else. */
export type Landing =
  | { kind: "pin" }
  /** A jump to a specific sentence — a chapter tap, a `#s=` deep link, a restored position. */
  | { kind: "sid"; sid: string }
  /** Forward navigation into a new chapter part. */
  | { kind: "start" }
  /** Backward navigation: land on the LAST page of the part we just entered. */
  | { kind: "end" };

export interface RepaginateCtx {
  paginator: Paginator;
  /**
   * §5.3's `ctx.spokenSid`. **Always null in P1** — there is no audio yet. P2's `useCueSync` sets
   * it, and that one assignment is the whole of what repagination needs to know about narration:
   * when the voice is reading, the pin is the *spoken* sentence, not the top visible one, so a
   * font-size change lands on what you are hearing rather than on what you happen to be looking at.
   */
  spokenSid: string | null;
  /** Where to land. Defaults to `{ kind: "pin" }`. */
  landing?: Landing;
  /** Writes every typography custom property and calls `paginator.applyGeometry()`. */
  applyTypography: () => void;
  /**
   * The typographic half of the stability signature: face, size, justification, spread preference.
   *
   * The *geometric* half — the column box the type was actually laid into — is appended by
   * `repaginate()` itself, **after** `applyTypography()` has run. It has to be: the caller only
   * knows the geometry of the layout that is being replaced, so a caller-supplied one would report
   * a resize as an instability. (It did. That is why this comment is here.)
   */
  typographySignature: string;
  /** For the beacon. */
  telemetry: LayoutTelemetryInput;
}

export interface LayoutTelemetryInput {
  scale: number;
  font: string;
  /**
   * ⚠️ These four are not optional decoration, and leaving them out does not degrade the beacon —
   * it deletes it. `POST /api/v1/telemetry/layout` runs `openBook(bookId)` (readAccess, even on a
   * write: without it anyone can append rows against any book id they can guess and the p95 this
   * endpoint exists to measure becomes unusable) and then refuses a body with no `editionId` with a
   * 400. A beacon with neither is a **404**, and `beaconLayout` swallows every failure on purpose,
   * so the whole of §13.1's instrumentation was firing into a wall with nothing in the console,
   * nothing on screen and a `listen_events` table that stayed empty. That was the shipped state
   * until a browser run counted the 404s.
   *
   * LAW 1 is the other half: a `sid` and a column count mean nothing without the edition they were
   * measured against, and this row is compared across editions in the rollup.
   */
  bookId: string;
  editionId: string;
  /** `sessions.id`-shaped. Stable per tab, so a p95 can be taken per reading session. */
  sessionId: string;
  chapterOrd: number;
  /**
   * Which `chapters.parts` slice is mounted. Reported, and — more importantly — part of the
   * instability key: see `noteColumnCount`.
   */
  part: number;
}

export interface RepaginateResult {
  /** The sid we pinned on, or null if the part had no sentences (an image plate, a title page). */
  pin: string | null;
  /** The column actually landed on. */
  column: number;
  pageCount: number;
  /** Cost of the forced layout + rect walk, in ms. The §13.1 falsifier is p95 > 150. */
  ms: number;
  /** True when an identical typography signature produced a different column count. */
  unstable: boolean;
  cause: RepaginateCause;
}

export function repaginate(ctx: RepaginateCtx, cause: RepaginateCause): RepaginateResult {
  const { paginator } = ctx;
  const landing = ctx.landing ?? { kind: "pin" };

  // ── 1. PIN FIRST ────────────────────────────────────────────────────────────
  const pin =
    landing.kind === "sid"
      ? landing.sid
      : ctx.spokenSid ?? paginator.topVisibleSid() ?? null;

  // ── 2. Kill the transition ──────────────────────────────────────────────────
  const track = paginator.track;
  const savedTransition = track.style.transition;
  track.style.transition = "none";

  try {
    // ── 3. Apply typography (the only write) ─────────────────────────────────
    ctx.applyTypography();

    // ── 4. ONE forced layout ─────────────────────────────────────────────────
    const m = paginator.measure();

    // ── 5. Land ──────────────────────────────────────────────────────────────
    let column: number;
    switch (landing.kind) {
      case "start":
        column = paginator.goTo(0, { animate: false });
        break;
      case "end":
        column = paginator.goTo(paginator.maxColumn(), { animate: false });
        break;
      default: {
        // `?? 0` is §5.3's own fallback and it is correct here in a way it would not be elsewhere:
        // a pin that is not in this part's column map means the part changed under us, and the top
        // of the part is the only defensible answer. It is NOT a silent reinterpretation of a sid
        // against a foreign edition (LAW 2) — that check lives in lib/position.ts, before a sid
        // ever reaches this function.
        const target = pin !== null ? paginator.columnForSid(pin) : undefined;
        column = paginator.goTo(target ?? 0, { animate: false });
        break;
      }
    }

    const g = paginator.geometry;
    /**
     * ⚠️ **The content is part of the key.** §13.1's falsifier is `scrollWidth` returning a different
     * column count for *the same text* at identical typography — a WebKit instability. Keyed on
     * typography and geometry alone, the key also collapses **different chapters**: mounting chapter
     * 10 (13 columns) and then chapter 11 (12 columns) at the same font and the same viewport is a
     * column-count change at an "identical signature", and it was reported as an instability.
     *
     * Measured: 2 of 54 beacons in one Chromium session, both `cause:"mount"`, both at 354×727 —
     * two different chapters. That is a false positive on the single number that decides whether the
     * riskiest bet in the product was right, and it arrives pre-armed as `layout.alarm: true` on
     * `/api/v1/status`. A falsifier that cries wolf on ordinary navigation is worse than no falsifier,
     * because the real firing is then indistinguishable from the noise.
     */
    const content = `${ctx.telemetry.chapterOrd}/${ctx.telemetry.part}`;
    const signature = `${content}|${ctx.typographySignature}|${g?.pageW ?? 0}x${g?.availH ?? 0}|${g?.spread ? "2" : "1"}`;
    const unstable = noteColumnCount(signature, m.pageCount);

    // ── 7. Beacon ────────────────────────────────────────────────────────────
    beaconLayout({
      bookId: ctx.telemetry.bookId,
      editionId: ctx.telemetry.editionId,
      sessionId: ctx.telemetry.sessionId,
      chapterOrd: ctx.telemetry.chapterOrd,
      part: ctx.telemetry.part,
      sid: pin,
      ms: m.ms,
      cols: m.pageCount,
      dpr: typeof devicePixelRatio === "number" ? devicePixelRatio : 1,
      w: g?.availW ?? 0,
      h: g?.availH ?? 0,
      scale: ctx.telemetry.scale,
      font: ctx.telemetry.font,
      cause,
      unstable,
    });

    return {
      pin,
      column,
      pageCount: m.pageCount,
      ms: m.ms,
      unstable,
      cause,
    };
  } finally {
    // ── 6. Restore the transition on the NEXT frame ──────────────────────────
    // In `finally` so a throw inside measure() cannot leave the reader permanently un-animated.
    requestAnimationFrame(() => {
      track.style.transition = savedTransition;
    });
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * The telemetry beacon.
 *
 * design.md §13.1: CSS multi-column pagination inside a transform-translated track, on mobile
 * Safari, is **the riskiest bet in the product** and it cannot be tested from amd-server — there
 * is no monitor and no iPhone. That is the entire reason this beacon ships in P1 and not later.
 *
 * The falsifiers, as numbers, so nobody has to argue about them:
 *   · p95 `ms` above **150** for a median chapter part on a real iPhone.
 *   · `cols` differing across two relayouts at identical typography — a `scrollWidth` instability.
 *     That is what `unstable` reports, and it is why `typographySignature` exists.
 * The fallback if either fires is JS pagination at block granularity with no intra-paragraph
 * splits: a visibly worse reader, which is exactly why the number gets measured in week one
 * rather than debated.
 * ══════════════════════════════════════════════════════════════════════════ */

export interface LayoutBeacon {
  /** Who and where. See `LayoutTelemetryInput` — without these the route refuses the row. */
  bookId: string;
  editionId: string;
  sessionId: string;
  chapterOrd: number;
  /** The mounted `chapters.parts` slice. Distinguishes two relayouts of two different texts. */
  part: number;
  /** The sentence the relayout pinned on. Optional to the route, and the most useful field in it. */
  sid?: string | null;
  ms: number;
  cols: number;
  dpr: number;
  w: number;
  h: number;
  scale: number;
  font: string;
  /** Not in §5.3's literal payload. A p95 that is 40 ms on `resize` and 900 ms on `mount` is two
   *  different findings, and without this field they are one number. */
  cause: RepaginateCause;
  /** The second falsifier, as a boolean, so it can be counted rather than eyeballed in a scatter. */
  unstable: boolean;
}

const TELEMETRY_URL = "/api/v1/telemetry/layout";

/** §5.3: "sampled 1-in-4 after ten per session". */
const ALWAYS_FIRST = 10;
const SAMPLE_EVERY = 4;
let sent = 0;
let seen = 0;

/** signature → the column count it produced last time, for the instability check. */
const lastCols = new Map<string, number>();

function noteColumnCount(signature: string, cols: number): boolean {
  const prev = lastCols.get(signature);
  lastCols.set(signature, cols);
  // Bounded: one entry per distinct (size, font, scale, geometry) combination a session reaches.
  // A reader who drags the window across every pixel width would otherwise accumulate one entry
  // per pixel, forever, in a tab that stays open for a week.
  if (lastCols.size > 64) {
    const oldest = lastCols.keys().next();
    if (!oldest.done) lastCols.delete(oldest.value);
  }
  return prev !== undefined && prev !== cols;
}

export function beaconLayout(payload: LayoutBeacon): void {
  seen++;
  // An instability is never sampled away. It is the finding the beacon exists to catch and it is
  // rare by construction; dropping three out of four of them is how a real falsifier reads as
  // noise.
  const forced = payload.unstable;
  if (!forced && seen > ALWAYS_FIRST && (seen - ALWAYS_FIRST) % SAMPLE_EVERY !== 0) return;

  const body = JSON.stringify({ ...payload, ms: Math.round(payload.ms * 10) / 10 });
  sent++;

  try {
    // `keepalive` so the last beacon of a session survives the tab closing. Not `sendBeacon`,
    // because sendBeacon cannot set `content-type: application/json` without a Blob and the route
    // is a normal JSON POST; the position *beacon* is the one that must be text/plain, and it
    // lives in lib/position.ts for exactly that reason.
    void fetch(TELEMETRY_URL, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => {
      // Telemetry must never surface to the reader. A book that shows an error because a metrics
      // POST failed is worse than no metrics.
    });
  } catch {
    /* fetch itself can throw synchronously on a torn-down document. Ignore. */
  }
}

/** Test/diagnostic hook. Not used by the reader. */
export function layoutBeaconStats(): { seen: number; sent: number } {
  return { seen, sent };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Relayout triggers that are not user actions.
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Resolve once every `<img>` currently in the track has real dimensions.
 *
 * An image that loads *after* the measure changes the track's height usage and therefore its
 * column count — and the reader has already landed, so the correction moves them mid-read. The
 * intrinsic `width`/`height` attributes that `renderBlock` emits mean the layout is usually right
 * before any byte arrives; this is the belt for the case where ingest recorded 0×0.
 *
 * The timeout is not optional: a broken image on a flaky connection must not hold the first
 * pagination hostage. On timeout we measure anyway and take the `load`-driven relayout when it
 * comes.
 */
export function imagesSettled(track: HTMLElement, timeoutMs = 2000): Promise<void> {
  const imgs = [...track.querySelectorAll("img")].filter((img) => !img.complete);
  if (imgs.length === 0) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let left = imgs.length;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve();
    };
    const one = () => {
      if (--left <= 0) finish();
    };
    const timer = setTimeout(finish, timeoutMs);
    for (const img of imgs) {
      img.addEventListener("load", one, { once: true });
      img.addEventListener("error", one, { once: true });
    }
  });
}

/**
 * The §5.2 pitfall-(1) resize handler, debounced 120 ms.
 *
 * Two sources, and both are needed. `visualViewport` reports the *visual* viewport — the thing
 * that changes when iOS shows the software keyboard or the user pinch-zooms — and it is the only
 * signal that fires on a rotation *before* layout settles. `ResizeObserver` on the stage reports
 * what the flex box actually gave us, which is what the frame is sized from. Listening to only
 * `window.resize` gets neither: it does not fire for a keyboard, and on iOS it fires at the wrong
 * moment during a rotation.
 *
 * The first call is NOT debounced. Waiting 120 ms before the first pagination is 120 ms of blank
 * frame on every cold open, and there is nothing to coalesce yet.
 */
export function onViewportChange(
  stage: Element,
  handler: (cause: RepaginateCause) => void,
  debounceMs = 120,
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let primed = false;
  let pending: RepaginateCause = "resize";
  let lastW = 0;
  let lastH = 0;

  const fire = (cause: RepaginateCause) => {
    if (!primed) {
      primed = true;
      handler(cause);
      return;
    }
    pending = cause;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      handler(pending);
    }, debounceMs);
  };

  const ro = new ResizeObserver((entries) => {
    const box = entries[0]?.contentRect;
    if (!box) return;
    const w = Math.round(box.width);
    const h = Math.round(box.height);
    if (w === lastW && h === lastH) return;
    /**
     * Rotation is worth distinguishing in telemetry: it is the case §5.2 pitfall (4) exists for, and
     * a p95 that is fine on `resize` and terrible on `rotate` points straight at
     * `-webkit-text-size-adjust`.
     *
     * ⚠️ **Test the ORIENTATION FLIP, not an axis swap.** This used to ask whether the new width
     * equalled the old height within 4 px and vice versa — which would be right if we observed the
     * viewport. We observe the **stage**, which is the viewport minus the reader's topbar and footer,
     * so the axes never cleanly swap. Measured on the real page: portrait `[390, 764]`, landscape
     * `[844, 310]` — `|844-764| = 80`, nowhere near 4. The consequence was not an error: across six
     * real rotations the beacon recorded `{mount, typography, fonts, part, resize}` and **`rotate: 0`
     * out of 107 rows**, so the discrimination the field exists for could not be made and nothing said
     * so.
     */
    const rotated = primed && lastW > 0 && lastH > 0 && w > 0 && h > 0 && w > h !== lastW > lastH;
    lastW = w;
    lastH = h;
    fire(rotated ? "rotate" : primed ? "resize" : "mount");
  });
  ro.observe(stage);

  const vv = typeof visualViewport !== "undefined" ? visualViewport : null;
  const onVv = () => fire("resize");
  vv?.addEventListener("resize", onVv);

  return () => {
    if (timer) clearTimeout(timer);
    ro.disconnect();
    vv?.removeEventListener("resize", onVv);
  };
}
